import { useState, useEffect } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
  CircularProgress,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import { Loading } from '../Loading';

interface RouterDailyPerformanceTestProps {
  cpeId: string;
  perfTestScheduled: boolean;
  perfTestHourOfDay: number;
  perfTestPeerIP: string;
  onSubmit: () => void;
}

const hour = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12,
  13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23
];

export const RouterDailyPerformanceTest = ({
  cpeId, perfTestScheduled, perfTestHourOfDay, perfTestPeerIP, onSubmit
}: RouterDailyPerformanceTestProps) => {
  const api = useApi();

  const [peerRegions, setPeerRegions] = useState<string[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>( perfTestPeerIP || '');
  const [hourRange, setHourRange] = useState<number[]>(hour);
  const [selectedHour, setSelectedHour] = useState<number>(perfTestHourOfDay ?? 0);
  const [isTesting, setIsTesting] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

  const PerfTest = useMutation(api.configurePerfTestScheduleApiV1ConfigurePerfTestSchedulePost, {
    onSuccess: () => {
      toast.success((isTesting ? 'Started' : 'Stopped') + ' the daily test successfully');
      onSubmit();
      setIsTesting(false);
    },
    onError: (error) => {
      setIsTesting(false);
      apiError(error);
    },
  });

  const { isLoading, data: ipsData } = useQuery(
    ['preparePeerRegion'],
    api.getPeerRegionsApiV1GetPeerRegionsGet.bind(null, {}),
    { enabled: true, cacheTime: 0, initialData: undefined },
  );

  useEffect(() => {
    if (ipsData != null && ipsData != undefined) {
      setPeerRegions(ipsData.data || []);
    }
  }, [ipsData]);

  const handleChange = ({ target: { value }}: SelectChangeEvent): void => {
    setSelectedRegion(value);
  };

  const handleHourChange = ({ target: { value }}: SelectChangeEvent): void => {
    setSelectedHour(parseInt(value));
  };

  const handleStart = () => {
    PerfTest.mutate({
      cpe_id: cpeId,
      peer_region: selectedRegion,
      hour_of_day: selectedHour,
      enable: isTesting,
    });
  };

  const handleStop = () => {
    PerfTest.mutate({
      cpe_id: cpeId,
      peer_region: selectedRegion || '',
      hour_of_day: selectedHour,
      enable: isTesting,
    });
  }

  const handleStartButton = () => {
    if (!selectedRegion) {
      toast.error("Select the Peer region and hour of day.");
    } else {
      setIsTesting(true);
      setShowConfirmDialog(true);
    }
  }

  const handleStopButton = () => {
    setShowConfirmDialog(true);
    setIsTesting(false);
  }

  if (isLoading) return <Loading />

  return (
    <Box pt={2}>
      <Box display="flex" justifyContent="center" sx={{ mb: 4 }}>
        <FormControl fullWidth>
          <InputLabel id="peer-ip-select-label">Peer region</InputLabel>
          <Select
            labelId="peer-ip-select-label"
            value={selectedRegion}
            label="Peer region"
            onChange={handleChange}
          >
            { peerRegions && peerRegions.map((item, i) => {
              return <MenuItem value={item} key={item}>{item}</MenuItem>;
            })}
          </Select>
        </FormControl>
      </Box>
      <Box display="flex" justifyContent="center" sx={{ mb: 4 }}>
        <FormControl fullWidth>
          <InputLabel id="hour-of-day-label">Hour of Day (UTC time)</InputLabel>
          <Select
            labelId="hour-of-day-label"
            value={selectedHour?.toString()}
            label="Hour of day (UTC time)"
            onChange={handleHourChange}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 200,
                  overflowY: 'auto',
                },
              },
            }}
          >
            { hourRange && hourRange.map((item, i) => {
              const formatHour = item.toString().padStart(2, '0');
              return <MenuItem value={item} key={item}>{`${formatHour}:00`}</MenuItem>;
            })}
          </Select>
        </FormControl>
      </Box>
      {showConfirmDialog && (
        <DialogConfirm
          open={showConfirmDialog}
          handleClose={() => setShowConfirmDialog(false)}
          handleConfirm={isTesting ? handleStart : handleStop}
          title={(isTesting ? 'Start' : 'Stop') + ' the daily test?'}
          description={'Are you certain you wish to proceed to ' + (isTesting ? 'start ': 'stop ') + ' the daily test?'}
        />
      )}
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        {(!showConfirmDialog && PerfTest.isLoading) && <CircularProgress sx={{ mr: 5 }}/> }
        <Button
          variant="outlined"
          onClick={() => {
            handleStartButton()
          }}
          disabled={perfTestScheduled}
          sx={{mr: 5}}
        >
          Start
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            handleStopButton()
          }}
          disabled={!perfTestScheduled}
        >
          Stop
        </Button>
      </Box>
    </Box>
  );
};
